// ── Shopping list ───────────────────────────────────────────────────
// Turns a stretch of the On Deck plan into one aggregated grocery list:
// every planned meal's ingredients, scaled to the portions planned, merged
// across recipes, and grouped into store aisles.
//
// Two things make the merge worth doing rather than just concatenating:
//   1. Units are canonicalised and converted within a family, so "500 g" from
//      one recipe and "1 kg" from another become a single "1.5 kg" line.
//   2. Names are matched on a loose (de-pluralised, punctuation-free) key, so
//      "onion" and "Onions" land on the same line. The DISPLAY name is always
//      the first spelling seen — normalisation only ever decides what merges.
//
// Amounts that aren't quantities ("to taste", "a pinch") don't total; the item
// is flagged `approx` so the UI can say the number is a floor, not a truth.

import { fmtQty, parseQty } from "./format";
import type { Ingredient, IngredientCategory, PlanDay, Recipe } from "./types";

// ── Units ───────────────────────────────────────────────────────────
// Spellings we accept, mapped to a canonical unit key.
const UNIT_ALIASES: Record<string, string> = {
  mg: "mg", milligram: "mg", milligrams: "mg",
  g: "g", gr: "g", gram: "g", grams: "g", gramme: "g", grammes: "g",
  kg: "kg", kilo: "kg", kilos: "kg", kilogram: "kg", kilograms: "kg",
  oz: "oz", ounce: "oz", ounces: "oz",
  lb: "lb", lbs: "lb", pound: "lb", pounds: "lb",
  ml: "ml", milliliter: "ml", milliliters: "ml", millilitre: "ml", millilitres: "ml",
  l: "l", liter: "l", liters: "l", litre: "l", litres: "l",
  tsp: "tsp", tsps: "tsp", teaspoon: "tsp", teaspoons: "tsp",
  tbsp: "tbsp", tbsps: "tbsp", tablespoon: "tbsp", tablespoons: "tbsp",
  "fl oz": "floz", floz: "floz", "fluid ounce": "floz", "fluid ounces": "floz",
  cup: "cup", cups: "cup",
  pt: "pint", pint: "pint", pints: "pint",
  qt: "quart", quart: "quart", quarts: "quart",
  gal: "gallon", gallon: "gallon", gallons: "gallon",
  // Countable units — no conversion, but plurals still need to merge.
  clove: "clove", cloves: "clove",
  can: "can", cans: "can",
  jar: "jar", jars: "jar",
  bottle: "bottle", bottles: "bottle",
  package: "package", packages: "package", pkg: "package", packet: "package", packets: "package",
  slice: "slice", slices: "slice",
  strip: "strip", strips: "strip",
  sprig: "sprig", sprigs: "sprig",
  stalk: "stalk", stalks: "stalk",
  stick: "stick", sticks: "stick",
  bunch: "bunch", bunches: "bunch",
  head: "head", heads: "head",
  fillet: "fillet", fillets: "fillet",
  piece: "piece", pieces: "piece",
  pinch: "pinch", pinches: "pinch",
  dash: "dash", dashes: "dash",
  handful: "handful", handfuls: "handful",
  sheet: "sheet", sheets: "sheet",
};

// Convertible unit families. `units` holds every unit's size in the family's
// base unit; `display` is the subset we're willing to show, largest first — a
// total is rendered in the biggest display unit that keeps it at 1 or above.
// (pint/quart/gallon convert IN but never render; nobody shops for "1 pint of
// stock" when the recipe said cups.)
const FAMILIES: Array<{
  id: string;
  units: Record<string, number>;
  display: string[];
}> = [
  { id: "mass-metric", units: { mg: 0.001, g: 1, kg: 1000 }, display: ["kg", "g"] },
  { id: "mass-imperial", units: { oz: 1, lb: 16 }, display: ["lb", "oz"] },
  { id: "vol-metric", units: { ml: 1, l: 1000 }, display: ["l", "ml"] },
  {
    id: "vol-us",
    units: { tsp: 1, tbsp: 3, floz: 6, cup: 48, pint: 96, quart: 192, gallon: 768 },
    display: ["cup", "tbsp", "tsp"],
  },
];

const FAMILY_OF: Record<string, (typeof FAMILIES)[number]> = {};
for (const f of FAMILIES) for (const u of Object.keys(f.units)) FAMILY_OF[u] = f;

// Units that read as words and therefore take a plural ("2 cloves", "3 cups").
// Abbreviations (g, kg, tbsp, oz…) never pluralise.
const WORD_UNITS = new Set([
  "cup", "pint", "quart", "gallon", "clove", "can", "jar", "bottle", "package",
  "slice", "strip", "sprig", "stalk", "stick", "bunch", "head", "fillet",
  "piece", "pinch", "dash", "handful", "sheet",
]);

export function canonicalUnit(raw: string | undefined): string | null {
  const u = (raw ?? "").trim().toLowerCase().replace(/\.$/, "");
  if (!u) return null;
  return UNIT_ALIASES[u] ?? u;
}

/**
 * Convert between two units of the same family — "24 oz" to lb, "500 g" to kg.
 *
 * Exists so the macro split (mealMacros.ts) can reconcile a recipe that says
 * ounces against a per-unit macro figure quoted per pound, without standing up
 * a second unit table that could drift from this one.
 *
 * Returns null when the conversion isn't meaningful: unknown units, or two
 * units from different families (grams to cups needs a density we don't have).
 * Same unit converts to itself even if it's a countable one — 3 cloves is 3
 * cloves — which is what makes "per clove" macros work.
 */
export function convertAmount(
  value: number,
  from: string | undefined,
  to: string | undefined
): number | null {
  const a = canonicalUnit(from);
  const b = canonicalUnit(to);
  // A bare count converts to a bare count; anything else needs real units.
  if (a === b) return value;
  if (!a || !b) return null;
  const fa = FAMILY_OF[a];
  const fb = FAMILY_OF[b];
  if (!fa || !fb || fa.id !== fb.id) return null;
  return (value * fa.units[a]) / fb.units[b];
}

function pluralize(unit: string, amount: number | null): string {
  if (!WORD_UNITS.has(unit) || amount === 1) return unit;
  return /(?:ch|sh|s|x)$/.test(unit) ? `${unit}es` : `${unit}s`;
}

// Conservative de-pluralisation of one word. "tomatoes" → "tomato", "cloves"
// → "clove"; "hummus", "couscous" and "boneless" keep their s. Only ever used
// to build a grouping key, never to display.
function singular(word: string): string {
  if (/(?:oes|ches|shes|xes)$/.test(word)) return word.replace(/es$/, "");
  if (word.length > 3 && /s$/.test(word) && !/(?:ss|us|is)$/.test(word)) {
    return word.slice(0, -1);
  }
  return word;
}

// Loose key for matching the same ingredient written two ways. Conservative
// de-pluralisation: only ever used to group, never to display.
//
// Exported because PlannedMeal.ingredientAmounts keys off it too — an override
// has to survive the recipe being edited from "Chicken Breasts" to "chicken
// breast", which an index or an exact name would not.
//
// That also means this function is a STORED key: corrections a cook has
// already made, and the macrosPer carry-over in mcp/main.py, are filed under
// whatever it returned at the time. It must stay word-order sensitive — see
// mergeKey below for the looser key the shopping list groups on.
export function normalizeName(name: string): string {
  const words = name
    .toLowerCase()
    .trim()
    .replace(/[.,;:!?"']/g, "")
    .replace(/\s+/g, " ")
    .split(" ");
  words[words.length - 1] = singular(words[words.length - 1]);
  return words.join(" ");
}

// ── Prep phrases ────────────────────────────────────────────────────
// "garlic cloves, minced" and "garlic cloves, grated" are one thing to buy
// and two things to do. The list is a shopping list, so the buying is the
// line and the doing is a note on it.
//
// The separator is a comma, but a comma alone is not enough: "boneless,
// skinless chicken thighs" splits into "boneless" and "skinless chicken
// thighs", and treating the first as the ingredient would be nonsense. So a
// trailing segment is only prep when EVERY word in it is a known prep word.
// "minced" qualifies; "skinless chicken thighs" does not, and stays put.

// Knife work and kitchen handling — things you do to the ingredient after
// buying it. Deliberately excludes anything that changes WHICH product you
// buy: canned, dried, fresh, frozen, smoked, roasted, ground. "black beans,
// canned" and "black beans" are two different tins.
const PREP_WORDS = new Set([
  "minced", "chopped", "sliced", "diced", "grated", "shredded", "smashed",
  "crushed", "halved", "quartered", "cubed", "julienned", "torn", "trimmed",
  "peeled", "seeded", "cored", "stemmed", "juiced", "zested", "beaten",
  "whisked", "drained", "rinsed", "softened", "melted", "divided", "packed",
  "separated", "reserved", "warmed", "rolled",
]);

// Adverbs and glue that may appear inside a prep phrase without making it
// something other than prep — "thinly sliced", "drained and rinsed".
const PREP_MODIFIERS = new Set([
  "finely", "roughly", "thinly", "thickly", "coarsely", "freshly", "lightly",
  "well", "very", "and", "or", "plus", "then", "about",
]);

// Leading words that describe the same purchase. Kept to a single, safe entry
// on purpose — "fresh cilantro" and "cilantro" are one bunch, but "ground
// ginger" and "ginger" are not, so nothing else joins this list without a
// reason.
const LEADING_DROP = new Set(["fresh"]);

function isPrepPhrase(segment: string): boolean {
  const words = segment.split(/\s+/).filter(Boolean);
  if (words.length === 0) return false;
  let sawPrep = false;
  for (const w of words) {
    const word = w.toLowerCase().replace(/[.;:!?"']/g, "");
    if (PREP_WORDS.has(word)) sawPrep = true;
    else if (!PREP_MODIFIERS.has(word)) return false;
  }
  // "and" on its own isn't a prep phrase; at least one real prep word.
  return sawPrep;
}

/**
 * Split an ingredient name into the thing you buy and the things you do to it.
 * Trailing comma-separated segments are peeled off while they read as prep,
 * and stop at the first that doesn't — "black beans, canned, drained and
 * rinsed" keeps its "canned" and loses only "drained and rinsed".
 */
export function splitPrep(name: string): { head: string; preps: string[] } {
  const parts = name.split(",").map((p) => p.trim()).filter(Boolean);
  const preps: string[] = [];
  while (parts.length > 1 && isPrepPhrase(parts[parts.length - 1])) {
    preps.unshift(parts.pop()!);
  }
  return { head: parts.join(", "), preps };
}

/**
 * The key the shopping list totals on: normalizeName, but de-pluralised word
 * by word and then sorted, so word order stops mattering.
 *
 * Recipes name the same thing in whatever order reads well — "boneless,
 * skinless chicken thighs" in one, "chicken thighs, boneless skinless" in the
 * next. Keyed on word order those are two ingredients, so a week planning both
 * put 1.5 lb on one line and 1.95 lb on another, filed under "b" and "c" in
 * the same aisle. Nothing was missing, but nothing said they were the same
 * bird either, and the cook reads it as one recipe's chicken gone astray.
 *
 * Deliberately NOT normalizeName itself: that one is stored, and re-ordering
 * it would orphan every per-meal correction already written.
 */
export function mergeKey(name: string): string {
  const { head } = splitPrep(name);
  return normalizeName(head)
    .split(" ")
    .filter((w) => !LEADING_DROP.has(w))
    .map(singular)
    .sort()
    .join(" ");
}

// ── Shape ───────────────────────────────────────────────────────────
export type ShoppingSource = {
  recipeId: string;
  title: string;
  meals: number; // how many planned meals in range call for this ingredient
};

export type ShoppingItem = {
  key: string;                  // stable across rebuilds — persists tick state
  name: string;                 // first spelling seen
  category: IngredientCategory;
  amount: number | null;        // total, in the family's base unit (null = unquantified)
  unit: string | null;          // canonical unit, null when the amount is a bare count
  approx: boolean;              // some source gave no number ("to taste")
  notes: string[];
  sources: ShoppingSource[];
};

export type ShoppingSection = {
  category: IngredientCategory;
  label: string;
  items: ShoppingItem[];
};

export type MissingMeal = { date: string; title: string };

export type ShoppingList = {
  sections: ShoppingSection[];
  itemCount: number;
  mealCount: number;
  recipeCount: number;
  missing: MissingMeal[]; // planned meals whose recipe is no longer in the cookbook
};

// Aisle order — the way you actually walk a store, not the enum order.
const AISLES: Array<{ category: IngredientCategory; label: string }> = [
  { category: "vegetable", label: "Vegetables" },
  { category: "fruit", label: "Fruit" },
  { category: "protein", label: "Protein" },
  { category: "dairy", label: "Dairy & eggs" },
  { category: "grain", label: "Grains & starch" },
  { category: "liquid", label: "Liquids & stock" },
  { category: "fat", label: "Oils & fats" },
  { category: "sweetener", label: "Sweeteners" },
  { category: "seasoning", label: "Spices & seasoning" },
  { category: "other", label: "Everything else" },
];

/** Render an item's total the way it should read on the list. */
export function formatAmount(item: ShoppingItem): string {
  if (item.amount == null) return "";
  if (!item.unit) return fmtQty(item.amount);
  const family = FAMILY_OF[item.unit];
  if (!family) return `${fmtQty(item.amount)} ${pluralize(item.unit, item.amount)}`;
  // Totals are carried in the family's base unit; step up to the largest
  // display unit that leaves at least 1 of it.
  const pick =
    family.display.find((u) => item.amount! / family.units[u] >= 1) ??
    family.display[family.display.length - 1];
  const value = item.amount / family.units[pick];
  return `${fmtQty(value)} ${pluralize(pick, value)}`;
}

/** One line of plain text for an item — used by "copy list". */
export function itemToText(item: ShoppingItem): string {
  const amt = formatAmount(item);
  const head = amt ? `${amt} ${item.name}` : item.name;
  return item.approx && amt ? `${head} (+ to taste)` : head;
}

/**
 * Aggregate the given plan days into a shopping list. `byId` is the cook's
 * cookbook — ingredient amounts and base servings are always resolved live
 * from it, so editing a recipe updates the list without touching the plan.
 */
export function buildShoppingList(
  days: PlanDay[],
  byId: Map<string, Recipe>
): ShoppingList {
  const items = new Map<string, ShoppingItem>();
  // Every spelling seen for a merged line, counted. "garlic" in two recipes and
  // "Garlic" in one should read as "garlic" — the majority wording, not
  // whichever recipe happened to be planned first.
  const spellings = new Map<string, Map<string, number>>();
  const missing: MissingMeal[] = [];
  const recipeIds = new Set<string>();
  let mealCount = 0;

  for (const day of days) {
    for (const meal of day.meals) {
      const recipe = byId.get(meal.recipeId);
      if (!recipe) {
        missing.push({ date: day.date, title: meal.title });
        continue;
      }
      mealCount += 1;
      recipeIds.add(recipe.id);
      // Ingredient amounts are written for the recipe's own yield; scale them
      // to the portions planned for this meal (same basis On Deck uses for
      // macros, which are stored per serving).
      const ratio = meal.servings / (recipe.servings || 1);
      for (const group of recipe.ingredientGroups ?? []) {
        for (const ing of group.ingredients ?? []) {
          addIngredient(items, spellings, ing, ratio, recipe, meal.ingredientAmounts);
        }
      }
    }
  }

  for (const [key, counts] of spellings) {
    const item = items.get(key);
    if (!item) continue;
    let best = item.name;
    let bestCount = 0;
    for (const [spelling, n] of counts) {
      if (n > bestCount) {
        best = spelling;
        bestCount = n;
      }
    }
    item.name = best;
  }

  const sections: ShoppingSection[] = [];
  for (const aisle of AISLES) {
    const inAisle = [...items.values()]
      .filter((i) => i.category === aisle.category)
      .sort((a, b) => a.name.localeCompare(b.name));
    if (inAisle.length > 0) sections.push({ ...aisle, items: inAisle });
  }

  return {
    sections,
    itemCount: items.size,
    mealCount,
    recipeCount: recipeIds.size,
    missing,
  };
}

function addIngredient(
  items: Map<string, ShoppingItem>,
  spellings: Map<string, Map<string, number>>,
  ing: Ingredient,
  ratio: number,
  recipe: Recipe,
  overrides?: Record<string, number>
) {
  const name = (ing.name ?? "").trim();
  if (!name) return;

  const unit = canonicalUnit(ing.unit);
  const family = unit ? FAMILY_OF[unit] : undefined;
  // Two keys, on purpose. The override is filed under the stored name key —
  // built from the FULL name, prep and all, because that is how the cook's
  // correction was filed — while the line it lands on is chosen by the
  // looser, word-order-blind, prep-stripped one.
  const nameKey = normalizeName(name);
  const { head, preps } = splitPrep(name);
  // Key on the family (not the unit) so grams and kilos of the same thing
  // share a line, while grams and cups stay honestly separate.
  const key = `${mergeKey(name)}|${family?.id ?? unit ?? "count"}`;

  // Count spellings of the HEAD: the line is named for what you buy, and the
  // three ways to cut a clove of garlic shouldn't each be a candidate name.
  const seen = spellings.get(key) ?? new Map<string, number>();
  seen.set(head, (seen.get(head) ?? 0) + 1);
  spellings.set(key, seen);

  const parsed =
    ing.base == null || ing.base === ""
      ? null
      : typeof ing.base === "number"
        ? ing.base
        : parseQty(String(ing.base));
  // An amount the cook corrected for this meal replaces the recipe's, and does
  // NOT take the serving ratio — it's already this meal's share, not the
  // recipe's yield. Buying 2 lb for three dinners puts 0.667 on each meal, and
  // the list totals back to the 2 lb actually needed.
  const override = overrides?.[nameKey];
  // Store the total in the family's base unit so conversion is a single step.
  const scaled =
    override != null
      ? override * (family ? family.units[unit!] : 1)
      : parsed == null
        ? null
        : parsed * ratio * (family ? family.units[unit!] : 1);

  const existing = items.get(key);
  if (!existing) {
    items.set(key, {
      key,
      name: head,
      category: ing.category ?? "other",
      amount: scaled,
      unit,
      approx: scaled == null,
      // The prep rides along as a note, so peeling it off the name never
      // loses it — "garlic cloves — minced; grated; smashed".
      notes: [...preps, ...(ing.note ? [ing.note] : [])],
      sources: [{ recipeId: recipe.id, title: recipe.title, meals: 1 }],
    });
    return;
  }

  if (scaled == null) existing.approx = true;
  else existing.amount = (existing.amount ?? 0) + scaled;
  for (const prep of preps) {
    if (!existing.notes.includes(prep)) existing.notes.push(prep);
  }
  if (ing.note && !existing.notes.includes(ing.note)) existing.notes.push(ing.note);
  const source = existing.sources.find((s) => s.recipeId === recipe.id);
  if (source) source.meals += 1;
  else existing.sources.push({ recipeId: recipe.id, title: recipe.title, meals: 1 });
}

/** The whole list as plain text, for the clipboard. */
export function listToText(
  list: ShoppingList,
  rangeLabel: string,
  extras: Array<{ name: string }> = []
): string {
  const lines = [`Shopping list — ${rangeLabel}`, ""];
  for (const section of list.sections) {
    lines.push(section.label.toUpperCase());
    for (const item of section.items) lines.push(`  - ${itemToText(item)}`);
    lines.push("");
  }
  if (extras.length > 0) {
    lines.push("ALSO GRAB");
    for (const e of extras) lines.push(`  - ${e.name}`);
    lines.push("");
  }
  return lines.join("\n").trimEnd();
}
